import { Resources } from 'constants/resources';

import { useBooleanChoices } from 'hooks/useBooleanChoices';
import isEqual from 'lodash/isEqual';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetList } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { FilterDrawerRow } from 'shared/mui/Filter/FilterDrawer';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  BooleanInput,
  EnumAutocompleteInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { cleanEmpty } from 'utils/cleanEmpty';

export const useOrdersListFilterFields = (): FilterFieldConfig[] => {
  const translate = useTypedTranslate();
  const booleanChoices = useBooleanChoices();

  const { control, watch, trigger, getValues, resetField, setValue } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters } = useFilterContext();

  const { data: orderStatuses } = useGetList(Resources.NewOrderStatuses);

  const merchantWatched = watch('merchant');
  const providerWatched = watch('provider');
  const gatewayWatched = watch('gateway');
  const externalErrorWatched = watch('orderProviderExternalError.providerExternalError.reason');

  const triggerAmountLteValidation = () => trigger(['amountPenny.lte']);
  const triggerAmountGteValidation = () => trigger(['amountPenny.gte']);

  const amountFromInput = (
    <TextInput
      helperText={false}
      label={translate('app.filter.amountFrom')}
      onChange={triggerAmountLteValidation}
      resettable
      source="amountPenny.gte"
      sx={{ minWidth: 'auto' }}
      variant="outlined"
    />
  );
  const amountToInput = (
    <TextInput
      helperText={false}
      label={translate('app.filter.amountTo')}
      onChange={triggerAmountGteValidation}
      resettable
      source="amountPenny.lte"
      sx={{ minWidth: 'auto' }}
      variant="outlined"
    />
  );

  return [
    {
      name: 'appliedDate',
      label: translate('app.filter.creationTime'),
      chipType: 'dateTimeRangeTimezone',
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTimeRangeTimezonePicker
              label={translate('app.filter.creationTimePeriod')}
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              sx={{
                marginBottom: 0,
              }}
              timezone={timezone}
              value={value}
            />
          )}
        />
      ),
    },
    {
      name: 'merchant',
      label: translate('app.filter.merchant'),
      chipType: 'autocomplete',
      chipProps: {
        resource: Resources.Merchants,
        onApply: () => {
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', []);

          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput perPage={100} reference={Resources.Merchants} source="merchant">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label={translate('app.filter.merchant')}
            {...(variant === 'field' && {
              onChange: (value: string | null) => {
                if (value === appliedFilters.merchant) {
                  return;
                }

                resetField('provider');
                resetField('gateway');
                resetField('method');
              },
            })}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'provider',
      label: translate('app.filter.provider'),
      chipType: 'autocomplete',
      chipProps: {
        resource: Resources.Providers,
        onApply: () => {
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', []);

          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          resetField('gateway', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('gateway', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          filter={cleanEmpty({
            merchantAccounts: { merchants: merchantWatched },
          })}
          perPage={100}
          reference={Resources.Providers}
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.provider')}
            name="provider"
            {...(variant === 'field' && {
              onChange: (value: string | null) => {
                if (value === appliedFilters.provider) {
                  return;
                }

                resetField('gateway');
                resetField('method');
              },
            })}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'gateway',
      label: translate('app.filter.gateways'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Gateways,
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('method', []);

          resetField('method', { keepDirty: true });
        },
        onDelete: () => {
          setAppliedFilter('method', []);

          resetField('method', { keepDirty: true });
        },
        onReset: () => {
          setValue('method', appliedFilters?.['method']);

          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={cleanEmpty({
            merchantAccounts: { merchants: merchantWatched },
            provider: providerWatched,
          })}
          perPage={100}
          reference={Resources.Gateways}
          source="gateway"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.gateways')}
            name="gateway"
            {...(variant === 'field' && {
              onChange: (value: string[]) => {
                if (
                  Boolean(appliedFilters.gateway?.length) &&
                  isEqual(value, appliedFilters.gateway)
                ) {
                  return;
                }

                resetField('method');
              },
            })}
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'method',
      label: translate('app.filter.methods'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Methods,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({
            gateway: gatewayWatched,
          })}
          perPage={100}
          reference={Resources.Methods}
          source="method"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label={translate('app.filter.methods')}
            name="method"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'direction',
      label: translate('app.filter.direction'),
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: Resources.Directions,
      },
      input: (
        <EnumAutocompleteInput
          filterToQuery={(searchText: string) => ({ description: searchText })}
          fullWidth
          helperText={false}
          label={translate('app.filter.direction')}
          name="direction"
          optionText="description"
          optionValue="value"
          resource={Resources.Directions}
          source="direction"
          variant="outlined"
        />
      ),
    },
    {
      name: 'status',
      label: translate('app.filter.statuses'),
      chipType: 'enumMultiselect',
      chipProps: {
        resource: Resources.NewOrderStatuses,
        limitTags: 1,
      },
      input: (
        <AutocompleteMultiselectInput
          defaultValue={getValues('status')}
          helperText={false}
          label={translate('app.filter.statuses')}
          name="status"
          optionText="description"
          optionValue="value"
          options={orderStatuses || []}
        />
      ),
    },
    {
      name: 'currency',
      label: translate('app.filter.currencies'),
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Currencies,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={{ active: true, groups: ['reference'] }}
          perPage={50}
          reference={Resources.Currencies}
          source="currency"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            helperText={false}
            label={translate('app.filter.currencies')}
            name="currency"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'customer.customerId',
      label: translate('app.filter.customer'),
      chipType: 'text',
      input: (
        <TextInput
          helperText={false}
          label={translate('app.filter.customer')}
          resettable
          source="customer.customerId"
          variant="outlined"
        />
      ),
    },
    {
      name: 'amountPenny',
      label: translate('app.table.amount'),
      chipType: 'fromTo',
      chipProps: {
        fromName: 'amountPenny.gte',
        toName: 'amountPenny.lte',
        fromSlot: amountFromInput,
        toSlot: amountToInput,
      },
      input: (
        <FilterDrawerRow>
          {amountFromInput}
          {amountToInput}
        </FilterDrawerRow>
      ),
    },
    {
      name: 'orderProviderExternalError.providerExternalError.reason',
      label: translate('app.filter.declineReason'),
      chipType: 'text',
      chipProps: {
        onDelete: () => {
          setAppliedFilter('matchRejectReason', null);
          resetField('matchRejectReason', { keepDirty: true });
        },
      },
      input: (variant) => (
        <TextInput
          helperText={false}
          label={translate('app.filter.declineReason')}
          {...(variant === 'field' && {
            onChange: (value: unknown) => {
              if (!value) {
                setValue('matchRejectReason', false, { shouldDirty: true });
              }
            },
          })}
          resettable
          source="orderProviderExternalError.providerExternalError.reason"
          variant="outlined"
        />
      ),
    },
    {
      name: 'matchRejectReason',
      label: translate('app.filter.matchRejectReason'),
      chipType: 'text',
      chipProps: {
        getName: (filterValue: boolean) =>
          booleanChoices.find((value) => value.id === filterValue)?.name || '',
        isNotIgnoreFalseValue: true,
      },
      input: (variant) => (
        <BooleanInput
          {...(variant === 'field' && {
            defaultValue: externalErrorWatched ? false : null,
            disabled: !externalErrorWatched,
          })}
          label={translate('app.filter.matchRejectReason')}
          source="matchRejectReason"
          variant="outlined"
        />
      ),
    },
  ];
};
